import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import api, { type Category } from "../lib/api";
import { useAuthStore } from "../store/auth";
import { useTenant } from "../lib/tenant";
import toast from "react-hot-toast";

export default function OnboardingPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const tenant = useTenant();
  const from = (location.state as { from?: string })?.from ?? "/shop";

  const [selected, setSelected] = useState<string[]>([]);
  const [marketing, setMarketing] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["categories", tenant.id],
    queryFn: () => api.get("/products/categories").then((r) => r.data),
  });

  const visible = categories.filter((c) => !c.hidden);

  function toggle(slug: string) {
    setSelected((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    );
  }

  function categoryLabel(cat: Category) {
    if (i18n.language.startsWith("ja") && cat.nameJa) return cat.nameJa;
    return cat.name;
  }

  async function handleSubmit(skip = false) {
    setSaving(true);
    try {
      await api.patch("/auth/me/preferences", {
        interests: skip ? [] : selected,
        marketingEmails: skip ? false : marketing,
      });
    } catch {
      toast.error("Could not save preferences.");
    } finally {
      setSaving(false);
      navigate(from, { replace: true });
    }
  }

  return (
    <main className="min-h-[80vh] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-lg">

        <div className="text-center mb-10">
          <h1 className="font-display text-3xl font-bold text-gray-900 mb-3">
            {t("onboarding.title")}
            {user?.name ? ` ${user.name.split(" ")[0]}` : ""}
          </h1>
          <p className="text-gray-500">{t("onboarding.subtitle")}</p>
        </div>

        {visible.length > 0 && (
          <section className="mb-8">
            <h2 className="font-semibold text-gray-900 mb-4">
              {t("onboarding.interestsHeading")}
            </h2>
            <div className="flex flex-wrap gap-2">
              {visible.map((cat) => {
                const active = selected.includes(cat.slug);
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => toggle(cat.slug)}
                    className="px-4 py-2 rounded-full border-2 text-sm font-medium transition-colors select-none"
                    style={
                      active
                        ? {
                            background: "rgb(var(--brand-color))",
                            borderColor: "rgb(var(--brand-color))",
                            color: "#fff",
                          }
                        : { borderColor: "#e5e7eb", color: "#374151" }
                    }
                  >
                    {categoryLabel(cat)}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        <section className="mb-10">
          <h2 className="font-semibold text-gray-900 mb-4">
            {t("onboarding.marketingHeading")}
          </h2>
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-gray-300 flex-shrink-0"
              style={{ accentColor: "rgb(var(--brand-color))" }}
              checked={marketing}
              onChange={(e) => setMarketing(e.target.checked)}
            />
            <span className="text-sm text-gray-700">{t("onboarding.marketingLabel")}</span>
          </label>
        </section>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={() => handleSubmit(true)}
            disabled={saving}
            className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            {t("onboarding.skip")}
          </button>
          <button
            type="button"
            onClick={() => handleSubmit(false)}
            disabled={saving}
            className="btn-primary flex-1"
          >
            {saving ? "…" : t("onboarding.continue")}
          </button>
        </div>

      </div>
    </main>
  );
}
